import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Grid, Paper, IconButton, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem,
  FormControl, InputLabel, Tooltip, Alert, CircularProgress, Divider,
  Card, CardContent, CardActions, Stepper, Step, StepLabel, Avatar,
  InputAdornment, FormControlLabel, Switch, Stack
} from '@mui/material';
import {
  CreditCard, AccountBalance, QrCode2, PaymentsOutlined, Add,
  Delete, Star, StarBorder, Edit, Upload, CheckCircle, Close,
  ArrowBack, ArrowForward, Wallet, Image as ImageIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
const ASSET_BASE_URL = API_BASE_URL.replace(/\/api\/v1\/?$/, '');

// ─── Constants ────────────────────────────────────────────────────────────────
const PM_TYPES = [
  { id: 'bank_card',     label: 'Bank Card',     icon: <CreditCard />,       color: '#2E7D32', desc: 'Visa / MasterCard / AMEX / UnionPay' },
  { id: 'paypal',        label: 'PayPal',        icon: <PaymentsOutlined />, color: '#003087', desc: 'PayPal balance or linked card' },
  { id: 'wechat_pay',    label: 'WeChat Pay',    icon: <QrCode2 />,          color: '#07C160', desc: '微信支付 — upload your QR code' },
  { id: 'alipay',        label: 'Alipay',        icon: <QrCode2 />,          color: '#00A1E9', desc: '支付宝 — upload your QR code' },
  { id: 'bank_transfer', label: 'Bank Transfer', icon: <AccountBalance />,   color: '#1565C0', desc: 'International wire transfer' },
];

const typeInfo = (id) => PM_TYPES.find((t) => t.id === id) || PM_TYPES[0];

// ─── Card helpers ────────────────────────────────────────────────────────────
const luhnCheck = (digits) => {
  if (!digits || digits.length < 12 || digits.length > 19) return false;
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = parseInt(digits.charAt(i), 10);
    if (isNaN(n)) return false;
    if (alt) { n *= 2; if (n > 9) n -= 9; }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
};

const detectCardType = (digits) => {
  if (!digits) return null;
  if (/^4/.test(digits)) return 'visa';
  if (/^(5[1-5]|2[2-7])/.test(digits)) return 'mastercard';
  if (/^3[47]/.test(digits)) return 'amex';
  if (/^(62|81)/.test(digits)) return 'unionpay';
  if (/^(6011|65|64[4-9])/.test(digits)) return 'discover';
  if (/^35/.test(digits)) return 'jcb';
  if (/^(36|30[0-5]|3095|38|39)/.test(digits)) return 'diners';
  return null;
};

const formatCardNumber = (digits, type) => {
  if (!digits) return '';
  // AMEX: 4-6-5, Diners: 4-6-4, others: 4-4-4-4(-3)
  if (type === 'amex') {
    return digits.replace(/^(\d{0,4})(\d{0,6})(\d{0,5}).*/, (_, a, b, c) =>
      [a, b, c].filter(Boolean).join(' '));
  }
  if (type === 'diners') {
    return digits.replace(/^(\d{0,4})(\d{0,6})(\d{0,4}).*/, (_, a, b, c) =>
      [a, b, c].filter(Boolean).join(' '));
  }
  return digits.match(/.{1,4}/g)?.join(' ') || '';
};

const isExpiryValid = (mmYY) => {
  if (!mmYY) return false;
  const m = /^(\d{2})\s*\/\s*(\d{2,4})$/.exec(mmYY.trim());
  if (!m) return false;
  const month = parseInt(m[1], 10);
  let year = parseInt(m[2], 10);
  if (year < 100) year += 2000;
  if (month < 1 || month > 12) return false;
  const expiry = new Date(year, month, 1);
  return expiry > new Date();
};

// ─── Masked display per type ──────────────────────────────────────────────────
const MethodSummary = ({ m }) => {
  const info = typeInfo(m.type);
  switch (m.type) {
    case 'bank_card':
      return (
        <Box>
          <Typography variant="body2" color="text.secondary">
            {m.cardType?.toUpperCase() || 'CARD'} •••• {m.cardLast4 || '????'}
          </Typography>
          {m.cardExpiry && (
            <Typography variant="caption" color="text.secondary">Expires {m.cardExpiry}</Typography>
          )}
          {m.cardName && (
            <Typography variant="caption" display="block" color="text.secondary">{m.cardName}</Typography>
          )}
          <Chip
            label={m.cardActive ? 'Active' : 'Inactive'}
            size="small"
            color={m.cardActive ? 'success' : 'error'}
            variant="outlined"
            icon={m.cardActive ? <CheckCircle /> : <Close />}
            sx={{ mt: 0.5 }}
          />
        </Box>
      );
    case 'paypal':
      return (
        <Typography variant="body2" color="text.secondary">
          {m.paypalEmail || 'No email on file'}
        </Typography>
      );
    case 'wechat_pay':
    case 'alipay':
      return (
        <Box>
          {m.accountId && (
            <Typography variant="body2" color="text.secondary">ID: {m.accountId}</Typography>
          )}
          {m.qrCodeImage
            ? <Chip label="QR Uploaded" size="small" color="success" variant="outlined" icon={<CheckCircle />} sx={{ mt: 0.5 }} />
            : <Chip label="No QR" size="small" color="warning" variant="outlined" sx={{ mt: 0.5 }} />
          }
        </Box>
      );
    case 'bank_transfer':
      return (
        <Box>
          {m.bankName && <Typography variant="body2" color="text.secondary">{m.bankName}</Typography>}
          {m.accountNo && (
            <Typography variant="caption" color="text.secondary">
              Acct: ••••{m.accountNo.slice(-4)}
            </Typography>
          )}
        </Box>
      );
    default:
      return null;
  }
};

// ─── Add Dialog Steps ─────────────────────────────────────────────────────────
const ADD_STEPS = ['Choose Type', 'Enter Details', 'Upload QR'];

const TypeStep = ({ selected, onSelect }) => (
  <Grid container spacing={2} sx={{ pt: 1 }}>
    {PM_TYPES.map((pm) => (
      <Grid item xs={12} sm={6} key={pm.id}>
        <Paper
          onClick={() => onSelect(pm.id)}
          sx={{
            p: 2, cursor: 'pointer', borderRadius: 2, border: '2px solid',
            borderColor: selected === pm.id ? pm.color : '#e0e0e0',
            bgcolor: selected === pm.id ? `${pm.color}12` : 'background.paper',
            transition: 'all 0.2s',
            '&:hover': { borderColor: pm.color, bgcolor: `${pm.color}08` }
          }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: selected === pm.id ? pm.color : '#f0f0f0', color: selected === pm.id ? 'white' : pm.color }}>
              {pm.icon}
            </Avatar>
            <Box>
              <Typography fontWeight={700}>{pm.label}</Typography>
              <Typography variant="caption" color="text.secondary">{pm.desc}</Typography>
            </Box>
            {selected === pm.id && <CheckCircle sx={{ color: pm.color, ml: 'auto' }} />}
          </Box>
        </Paper>
      </Grid>
    ))}
  </Grid>
);

const DetailsStep = ({ type, form, setForm }) => {
  const field = (label, key, props = {}) => (
    <Grid item xs={props.xs || 12}>
      <TextField
        fullWidth label={label} size="small"
        value={form[key] || ''}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        {...props}
      />
    </Grid>
  );

  return (
    <Grid container spacing={2} sx={{ pt: 1 }}>
      {field('Label (e.g. My Visa Card)', 'label', { required: true, helperText: 'A nickname to identify this method' })}

      {type === 'bank_card' && (<>
        {field('Cardholder Name', 'cardName')}
        <Grid item xs={12}>
          <TextField
            fullWidth size="small"
            label="Card Number"
            required
            placeholder="1234 5678 9012 3456"
            value={form.cardNumberDisplay || ''}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '').slice(0, 19);
              const detected = detectCardType(digits);
              setForm((f) => ({
                ...f,
                cardNumber: digits,
                cardNumberDisplay: formatCardNumber(digits, detected || f.cardType),
                cardLast4: digits.slice(-4),
                cardType: detected || f.cardType || 'visa'
              }));
            }}
            error={!!form.cardNumber && form.cardNumber.length >= 12 && !luhnCheck(form.cardNumber)}
            helperText={
              !form.cardNumber
                ? 'Enter the full 13–19 digit card number'
                : form.cardNumber.length < 12
                  ? `${form.cardNumber.length} / 12+ digits`
                  : luhnCheck(form.cardNumber)
                    ? 'Valid card number'
                    : 'Invalid card number'
            }
            inputProps={{ inputMode: 'numeric', autoComplete: 'cc-number' }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth size="small"
            label="Expiry MM/YY"
            required
            placeholder="MM/YY"
            value={form.cardExpiry || ''}
            onChange={(e) => {
              let v = e.target.value.replace(/\D/g, '').slice(0, 4);
              if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2);
              setForm((f) => ({ ...f, cardExpiry: v }));
            }}
            error={!!form.cardExpiry && form.cardExpiry.length >= 5 && !isExpiryValid(form.cardExpiry)}
            helperText={
              !form.cardExpiry
                ? ' '
                : form.cardExpiry.length < 5
                  ? ' '
                  : isExpiryValid(form.cardExpiry)
                    ? 'Card not expired'
                    : 'Card expired or invalid'
            }
            inputProps={{ inputMode: 'numeric', autoComplete: 'cc-exp' }}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Card Type</InputLabel>
            <Select label="Card Type" value={form.cardType || 'visa'}
              onChange={(e) => setForm((f) => ({ ...f, cardType: e.target.value }))}>
              {['visa', 'mastercard', 'amex', 'unionpay', 'discover', 'jcb', 'diners'].map((c) => (
                <MenuItem key={c} value={c}>{c.toUpperCase()}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </>)}

      {type === 'paypal' && (
        field('PayPal Email Address', 'paypalEmail', { type: 'email', required: true })
      )}

      {(type === 'wechat_pay' || type === 'alipay') && (
        field(type === 'wechat_pay' ? 'WeChat ID (optional)' : 'Alipay Account / Phone (optional)', 'accountId')
      )}

      {type === 'bank_transfer' && (<>
        {field('Bank Name', 'bankName', { required: true })}
        {field('Account Holder Name', 'accountName', { required: true })}
        {field('Account Number', 'accountNo', { required: true })}
        {field('SWIFT / BIC Code', 'swiftCode', { xs: 6 })}
        {field('IBAN', 'iban', { xs: 6 })}
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel>Currency</InputLabel>
            <Select label="Currency" value={form.bankCurrency || 'USD'}
              onChange={(e) => setForm((f) => ({ ...f, bankCurrency: e.target.value }))}>
              {['USD', 'EUR', 'GBP', 'CNY', 'AED', 'SAR'].map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </>)}

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch checked={!!form.isDefault}
              onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))} />
          }
          label="Set as default payment method"
        />
      </Grid>
    </Grid>
  );
};

const QrStep = ({ type, methodId, onDone }) => {
  const fileRef = useRef();
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const requiresQr = type === 'wechat_pay' || type === 'alipay';

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!fileRef.current?.files[0]) { onDone(); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('qr', fileRef.current.files[0]);
      await api.post(`/payment-methods/${methodId}/qr`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('QR code uploaded!');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (!requiresQr) {
    return (
      <Box textAlign="center" py={4}>
        <CheckCircle sx={{ fontSize: 60, color: 'success.main' }} />
        <Typography variant="h6" mt={2}>Payment method saved!</Typography>
        <Typography color="text.secondary">No QR code needed for this payment type.</Typography>
      </Box>
    );
  }

  const info = typeInfo(type);

  return (
    <Box sx={{ pt: 1 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Upload your {info.label} QR code image. Customers will use this saved photo during checkout.
      </Alert>
      <Box
        onClick={() => fileRef.current.click()}
        sx={{
          border: '2px dashed', borderColor: preview ? info.color : '#bdbdbd',
          borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer',
          bgcolor: preview ? `${info.color}08` : '#fafafa',
          transition: 'all 0.2s', '&:hover': { borderColor: info.color }
        }}>
        {preview
          ? <Box component="img" src={preview} alt="QR preview"
              sx={{ maxHeight: 220, maxWidth: '100%', borderRadius: 1 }} />
          : <>
              <ImageIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary" mt={1}>
                Click to select QR code image
              </Typography>
              <Typography variant="caption" color="text.disabled">PNG, JPG up to 10 MB</Typography>
            </>
        }
      </Box>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} />
      {preview && (
        <Button size="small" sx={{ mt: 1 }} onClick={() => { setPreview(null); fileRef.current.value = ''; }}>
          Clear
        </Button>
      )}
      <Box display="flex" gap={2} mt={2}>
        <Button variant="outlined" onClick={onDone} sx={{ flex: 1 }} disabled={uploading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleUpload} sx={{ flex: 1 }} disabled={uploading || !preview}>
          {uploading ? <CircularProgress size={20} /> : 'Upload QR'}
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
        QR photo is required for WeChat Pay and Alipay payment methods.
      </Typography>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const CustomerPaymentMethods = () => {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [selectedType, setSelectedType] = useState('bank_card');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [newMethodId, setNewMethodId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const fetchMethods = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/payment-methods');
      setMethods(data.data);
    } catch (err) {
      toast.error('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMethods(); }, []);

  const openAdd = () => {
    setStep(0);
    setSelectedType('bank_card');
    setForm({});
    setNewMethodId(null);
    setDialogOpen(true);
  };

  const handleNext = async () => {
    if (step === 0) {
      setStep(1);
    } else if (step === 1) {
      if (!form.label?.trim()) { toast.warning('Please enter a label'); return; }
      if (selectedType === 'bank_card') {
        if (!form.cardNumber || form.cardNumber.length < 12) {
          toast.warning('Please enter a valid card number');
          return;
        }
        if (!luhnCheck(form.cardNumber)) {
          toast.error('Invalid card number');
          return;
        }
        if (!form.cardExpiry || !/^\d{2}\/\d{2,4}$/.test(form.cardExpiry)) {
          toast.warning('Please enter expiry as MM/YY');
          return;
        }
        if (!isExpiryValid(form.cardExpiry)) {
          toast.error('Card is expired');
          return;
        }
      }
      setSaving(true);
      try {
        // Strip display-only field before sending
        const { cardNumberDisplay, ...payload } = form;
        const { data } = await api.post('/payment-methods', { type: selectedType, ...payload });
        setNewMethodId(data.data._id);
        setStep(2);
        await fetchMethods();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to save');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleQrDone = () => {
    fetchMethods();
    setDialogOpen(false);
    toast.success('Payment method added!');
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/payment-methods/${id}`);
      setMethods((m) => m.filter((x) => x._id !== id));
      toast.success('Payment method removed');
    } catch (err) {
      toast.error('Failed to delete');
    } finally {
      setDeleteId(null);
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await api.put(`/payment-methods/${id}/default`);
      await fetchMethods();
      toast.success('Default updated');
    } catch (err) {
      toast.error('Failed to update default');
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Wallet sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Payment Methods</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your saved payment methods for faster checkout
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd} sx={{ borderRadius: 2 }}>
          Add Method
        </Button>
      </Box>

      {/* List */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : methods.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <Wallet sx={{ fontSize: 72, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No payment methods saved yet</Typography>
          <Typography variant="body2" color="text.disabled" mb={3}>
            Add a card, PayPal, WeChat Pay or bank account for faster checkout
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd}>
            Add Your First Method
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {methods.map((m) => {
            const info = typeInfo(m.type);
            return (
              <Grid item xs={12} sm={6} md={4} key={m._id}>
                <Card variant="outlined" sx={{
                  borderRadius: 3, border: '2px solid',
                  borderColor: m.isDefault ? info.color : '#e0e0e0',
                  position: 'relative', transition: 'all 0.2s',
                  '&:hover': { borderColor: info.color, boxShadow: `0 4px 20px ${info.color}30` }
                }}>
                  {m.isDefault && (
                    <Chip
                      label="Default"
                      size="small"
                      color="primary"
                      icon={<Star />}
                      sx={{
                        position: 'absolute', top: 10, right: 10,
                        fontWeight: 700, fontSize: '0.7rem'
                      }}
                    />
                  )}
                  <CardContent sx={{ pb: 1 }}>
                    {/* Type icon + label */}
                    <Box display="flex" alignItems="center" gap={2} mb={1.5}>
                      <Avatar sx={{ bgcolor: `${info.color}20`, color: info.color, width: 44, height: 44 }}>
                        {info.icon}
                      </Avatar>
                      <Box>
                        <Typography fontWeight={700} variant="subtitle1">{m.label}</Typography>
                        <Chip label={info.label} size="small" variant="outlined"
                          sx={{ borderColor: info.color, color: info.color, fontSize: '0.7rem' }} />
                      </Box>
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <MethodSummary m={m} />

                    {/* WeChat / Alipay QR preview */}
                    {(m.type === 'wechat_pay' || m.type === 'alipay') && m.qrCodeImage && (
                      <Box mt={1.5} sx={{ textAlign: 'center' }}>
                        <Box
                          component="img"
                          src={`${ASSET_BASE_URL}${m.qrCodeImage}`}
                          alt="QR Code"
                          sx={{ maxHeight: 120, maxWidth: '100%', borderRadius: 1, border: '1px solid #e0e0e0' }}
                        />
                      </Box>
                    )}
                  </CardContent>

                  <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
                    {!m.isDefault && (
                      <Button size="small" startIcon={<StarBorder />}
                        onClick={() => handleSetDefault(m._id)}>
                        Set Default
                      </Button>
                    )}
                    <Box flex={1} />
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteId(m._id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ─── Add Dialog ─── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Wallet color="primary" />
          Add Payment Method
          <IconButton sx={{ ml: 'auto' }} onClick={() => setDialogOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stepper activeStep={step} sx={{ mb: 3 }}>
            {ADD_STEPS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>

          {step === 0 && (
            <TypeStep selected={selectedType} onSelect={setSelectedType} />
          )}
          {step === 1 && (
            <DetailsStep type={selectedType} form={form} setForm={setForm} />
          )}
          {step === 2 && (
            <QrStep type={selectedType} methodId={newMethodId} onDone={handleQrDone} />
          )}
        </DialogContent>

        {step < 2 && (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => step === 0 ? setDialogOpen(false) : setStep(0)}
              startIcon={<ArrowBack />}>
              {step === 0 ? 'Cancel' : 'Back'}
            </Button>
            <Button variant="contained" onClick={handleNext}
              disabled={saving}
              endIcon={saving ? <CircularProgress size={16} /> : <ArrowForward />}>
              {step === 0 ? 'Continue' : 'Save Method'}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* ─── Delete Confirm ─── */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>Remove Payment Method?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            This payment method will be permanently removed. You can add it again later.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteId)}
            startIcon={<Delete />}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerPaymentMethods;
